import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, Link } from 'react-router-dom';
import { Spinner, Alert, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import TransactionDetails from 'components/transactions/TransactionDetails';
import { useTransactionDetailsData } from 'hooks/transactions/useTransactionDetailsData';
import { useTransactionProductsData } from 'hooks/transactions/useTransactionProductsData';

/**
 * TransactionDetail Page - Displays detailed information for a specific transaction
 * Shows orders associated with the transaction and summary metrics
 */
const TransactionDetail = () => {
  const { t } = useTranslation();
  // Get the entity transaction ID from the URL parameters
  const { transactionId } = useParams<{ transactionId: string }>();
  
  // State to control the content visibility
  const [contentReady, setContentReady] = useState<boolean>(false);
  
  // Ensure we have a valid transaction ID to pass to our hooks
  const validTransactionId = transactionId && transactionId.trim() !== '' ? transactionId : '';
  
  // Use our custom hooks to fetch transaction details and products
  const { orders, transactionInfo, loading: ordersLoading, error: ordersError } = useTransactionDetailsData(validTransactionId);
  const { products, loading: productsLoading, error: productsError, transactionId: queryTransactionId, orderTotals } = useTransactionProductsData(validTransactionId);
  
  // Combined loading and error states
  const loading = ordersLoading || productsLoading;
  const error = ordersError || productsError;
  
  // Effect to manage the transition state
  useEffect(() => {
    // Only show content when loading completes
    if (!loading) {
      // Slight delay to ensure all data is processed
      const timer = setTimeout(() => {
        setContentReady(true);
      }, 100);
      
      return () => clearTimeout(timer);
    } else {
      // Reset content ready state when loading starts
      setContentReady(false);
    }
  }, [loading]);
  
  return (
    <div className="mb-9">
      {/* Page Header */}
      <div className="mb-5">
        <h2 className="mb-2">{t("transactions.detail.title", "Transaction")} #{transactionInfo?.transaction_id || transactionId}</h2>
        
        {/* Back button */}
        <Button 
          variant="link" 
          className="text-decoration-none p-0 mb-4"
          as={Link}
          to="/transactions"
        >
          <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
          {t("transactions.detail.backButton", "Back to Transactions")}
        </Button>
      </div>
      
      {/* Loading state */}
      {loading && (
        <div className="d-flex justify-content-center align-items-center" style={{ height: '200px' }}>
          <Spinner animation="border" role="status" variant="warning">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      )}
      
      {/* Error state */}
      {error && (
        <Alert variant="danger" className="mb-4">
          <Alert.Heading>{t("transactions.detail.errorHeading", "Error Loading Transaction Details")}</Alert.Heading>
          <p>
            {t("transactions.detail.errorText", "There was a problem loading the details for this transaction. Please try again later or contact support if the problem persists.")}
          </p>
          <hr />
          <div className="d-flex justify-content-end">
            <Button 
              variant="outline-danger" 
              onClick={() => window.location.reload()}
            >
              {t("common.retry", "Retry")}
            </Button>
          </div>
        </Alert>
      )}
      
      {/* Only render content once it's stable (after loading) */}
      {!loading && !error && contentReady && (
        <>
          {/* Transaction Details */}
          {orders.length > 0 ? (
            <div>
              <TransactionDetails 
                orders={orders} 
                products={products}
                productsLoading={productsLoading}
                productsError={productsError}
                transactionDate={transactionInfo?.transaction_date || ''}
                transactionId={transactionInfo?.transaction_id || queryTransactionId || transactionId || ''}
                orderTotals={orderTotals}
              />
            </div>
          ) : (
            /* No orders found state */
            <Alert variant="info" className="mb-4">
              <Alert.Heading>No Orders Found</Alert.Heading>
              <p>
                No orders were found for this transaction. This may be because:
              </p>
              <ul>
                <li>The transaction ID is incorrect</li>
                <li>The transaction has no associated orders</li>
                <li>The data is still being processed</li>
              </ul>
            </Alert>
          )}
        </>
      )}

    </div>
  );
};

export default TransactionDetail;
